import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { 
getCategoriesNameById,
getFoodTypeById,
formatCreatedAtDate,
formatNumberWithComma
} from '../../utils/helpers';

// Export to CSV with all attributes
export const exportToCSV = async (data, branchNames) => {
try {
    const fileName = `food_categories_${new Date().toISOString().split('T')[0]}.csv`;
    
    const csvData = data.map(item => ({
    'Date': formatCreatedAtDate(item.created_at),
    'Food Category': item.name,
    'Description': item.description || '',
    'Status': item.status === 1 ? 'Active' : 'Inactive',
    'Branch': getCategoriesNameById(item.branch_id, branchNames || []) || 'N/A'
    }));

    const csvHeaders = Object.keys(csvData[0]);
    const csvRows = csvData.map(row => 
    csvHeaders.map(header => {
        const value = row[header] || '';
        const escapedValue = String(value).replace(/"/g, '""');
        return escapedValue.includes(',') ? `"${escapedValue}"` : escapedValue;
    }).join(',')
    );
    
    const csvString = [csvHeaders.join(','), ...csvRows].join('\r\n');
    const BOM = '\uFEFF';
    const csvContent = BOM + csvString;
    
    // Create and download without FileSaver
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return Promise.resolve();
} catch (error) {
    return Promise.reject(error);
}
};

// Export to Excel with all attributes
export const exportToExcel = async (data, branchNames) => {
try {
    const fileExtension = '.xlsx';
    const fileName = `food_categories_${new Date().toISOString().split('T')[0]}${fileExtension}`;
    
    const wsData = [
    ['Date Created', 'Food Category Name', 'Description', 'Status', 'Branch'],
    ...data.map(item => [
        formatCreatedAtDate(item.created_at),
        item.name,
        item.description || '',
        item.status === 1 ? 'Active' : 'Inactive',
        getCategoriesNameById(item.branch_id, branchNames || []) || 'N/A'
    ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    const wscols = [
    { wch: 20 }, // Date Created
    { wch: 30 }, // Food Category Name
    { wch: 50 }, // Description
    { wch: 15 }, // Status
    { wch: 25 }  // Branch
    ];
    ws['!cols'] = wscols;

    const wb = { Sheets: { 'Food Categories': ws }, SheetNames: ['Food Categories'] };
    
    // Use XLSX.writeFile to download directly
    XLSX.writeFile(wb, fileName);
    
    return Promise.resolve();
} catch (error) {
    return Promise.reject(error);
}
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
const words = text.split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Manual table drawing function for PDF (Portrait mode)
const drawFoodCategoriesTable = (doc, data, startY, branchNames) => {
const pageWidth = doc.internal.pageSize.width;
const pageHeight = doc.internal.pageSize.height;
const margin = 20;
const rowHeight = 12;

// Column widths for portrait mode - only Date, Food Category Name, Branch
const colWidths = [30, 70, 50];

let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;

// Check if we need a new page
if (currentY + neededHeight > pageHeight - 30) {
    doc.addPage();
    currentY = margin;
}

// Draw table header
doc.setFillColor(25, 118, 210); // Blue header
doc.setTextColor(255, 255, 255); // White text
doc.setFont(undefined, 'bold');

const headers = ['Date Created', 'Food Category Name', 'Branch'];
let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > pageHeight - 30) {
    doc.addPage();
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Draw vertical lines
    let verticalX = margin;
    for (let i = 0; i <= colWidths.length; i++) {
    doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
    if (i < colWidths.length) verticalX += colWidths[i];
    }
    
    // Draw horizontal lines
    doc.line(margin, currentY, pageWidth - margin, currentY);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Export to PDF with specified columns only (Portrait mode)
export const exportToPDF = async (data, filters = {}, branchNames) => {
return new Promise((resolve, reject) => {
    try {
    // Create PDF in portrait mode
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let currentY = margin;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text("Food Categories Report", pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    // Date and time
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, currentY);
    currentY += 8;
    
    // Summary
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Categories: ${data.length}`, margin, currentY);
    currentY += 8;
    
    // Apply filters text
    const filterTexts = [];
    if (filters.name) filterTexts.push(`Name: ${filters.name}`);
    if (filters.dateFrom) filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
    if (filters.dateTo) filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
    if (filters.searchTerm) filterTexts.push(`Search: ${filters.searchTerm}`);
    
    if (filterTexts.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const filtersText = `Filters: ${filterTexts.join(', ')}`;
        const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
        
        filtersLines.forEach(line => {
        if (currentY > pageHeight - 40) {
            doc.addPage();
            currentY = margin;
        }
        doc.text(line, margin, currentY);
        currentY += 6;
        });
        
        currentY += 10;
    }
    
    // Prepare table data with selected columns only: Date, Food Category Name, Branch
    const tableData = data.map(item => [
        formatCreatedAtDate(item.created_at),
        item.name,
        getCategoriesNameById(item.branch_id, branchNames || []) || 'N/A'
    ]);
    
    // Draw the table
    drawFoodCategoriesTable(doc, tableData, currentY + 10, branchNames);
    
    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }
    
    const fileName = `food_categories_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    resolve();
    } catch (error) {
    reject(error);
    }
});
};